/* eslint-disable react-refresh/only-export-components --
   AccessProvider and useAccess are intentionally co-located; same context
   pattern already used by lib/auth.jsx. */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { api, setWriteObserver } from './api.js'
import { useAuth } from './auth.jsx'
import {getSessionGeneration, onSessionGenerationChange} from './sessionGeneration.js'
import {
  ROLES,
  ROLE_META,
  ACTIONS,
  ACTION_META,
  can as rawCan,
  canActAs as rawCanActAs,
  denialReason as rawDenialReason,
  makeContext,
  normalizeResource,
  normalizeRole,
  visibleNav as rawVisibleNav,
} from './rbac.js'
import { recordAudit, pendingCount, sinkStatus } from './audit.js'
import { resolveLaneDelegation } from '../features/command-center/laneDelegation.js'
import {
  MANAGE_ASSIGNMENTS,
  resolveManageAssignments,
  setOverride as setAssignmentOverride,
  userSubject,
  roleSubject,
} from './assignmentPermissions.js'

/**
 * The access layer — one provider that resolves the actor's role, holds the
 * "acting as" state, and hooks every write to the audit trail. Modules consume
 * it through `useAccess()` and never re-derive a permission themselves.
 *
 * Remember what this is: a UX layer. See the security note at the top of
 * lib/rbac.js and the backend contract in RBAC.md.
 */

const AccessContext = createContext(null)

const ACTING_KEY = 'sr_acting_as'

function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

/**
 * Role preview, e.g. `?demo=1&role=ops`. Gated behind the repo's existing
 * `?demo=1` convention so it cannot be mistaken for a real grant, and it is
 * harmless regardless: the client's role only decides what gets DRAWN. The
 * server must not accept a role from the client, ever.
 */
function previewRole() {
  if (typeof window === 'undefined' || !isDemo()) return null
  const raw = new URLSearchParams(window.location.search).get('role')
  if (!raw) return null
  const r = normalizeRole(raw)
  return r === ROLES.UNSCOPED && raw.toLowerCase() !== 'unscoped' ? null : r
}

function loadActing() {
  try {
    const raw = sessionStorage.getItem(ACTING_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveActing(value) {
  try {
    if (value) sessionStorage.setItem(ACTING_KEY, JSON.stringify(value))
    else sessionStorage.removeItem(ACTING_KEY)
  } catch {
    /* storage unavailable — acting-as simply won't survive a reload */
  }
}

onSessionGenerationChange(() => { saveActing(null) })

export function AccessProvider({ children }) {
  const { user, isAuthed } = useAuth()

  // Role + book, enriched from the backend when it grows the route.
  /* ⚠️ `bookMeta` IS WHY A COUNT IS WHAT IT IS, AND IT WAS BEING DISCARDED.
   *
   * /api/access/me has carried `{ source, detail, degraded, count, accounts, at }`
   * alongside `book` — the backend's own comment says it exists "so a rep can see
   * WHY an account is (or isn't) in their book instead of guessing, and so a
   * degraded source is visible, not silent". This provider read `payload.book`
   * and threw the provenance away, so the client could tell you a rep's count was
   * zero and had nothing with which to say whether that meant "none exist",
   * "scoped to a book that is empty", or "the book source is degraded".
   *
   * Measured live 2026-10-02T12:09:54Z as a service identity: role `unscoped`,
   * book length 0, bookMeta.source `n/a`, detail "unscoped is scoped by lane, not
   * by account". That is the contract already answering the question. */
  const [grant, setGrant] = useState({
    role: null, book: null, bookMeta: null, permissions: null, source: 'session', loadError: null,
  })
  const [actingAs, setActingAs] = useState(() => loadActing())
  const [auditState, setAuditState] = useState({ pending: 0, sink: null })
  // Bumped whenever an exec flips a "Manage Assignments" override, so every
  // gated control re-decides immediately instead of after a reload.
  const [grantTick, setGrantTick] = useState(0)
  const refreshPermissions = useCallback(() => { setGrantTick(n => n + 1) }, [])
  useEffect(() => {
    window.addEventListener('focus', refreshPermissions)
    return () => window.removeEventListener('focus', refreshPermissions)
  }, [refreshPermissions])

  /* -- Resolve the role ------------------------------------------------ */

  useEffect(() => {
    if (!isAuthed) {
      setGrant({ role: null, book: null, bookMeta: null, permissions: null, source: 'session', loadError: null })
      return
    }
    let cancelled = false
    api
      .accessMe()
      .then((res) => {
        if (cancelled || !res) return
        const payload = res.access || res.user || res
        setGrant({
          role: payload.role ?? payload.sr_role ?? null,
          book:
            payload.book ||
            payload.accounts ||
            payload.assignedAccounts ||
            payload.assigned_accounts ||
            null,
          // EXPLICIT per-permission grants, when the backend carries any. This
          // is the durable half of the Manage Assignments toggle; absent today,
          // which is exactly why the toggle labels a local grant as local.
          permissions: payload.permissions || payload.grants || null,
          /* The provenance, carried through verbatim. Not normalized and not
           * defaulted: an absent bookMeta must stay absent so the UI says "not
           * reported" rather than inventing a source. */
          bookMeta: payload.bookMeta || null,
          schoolTeamCoverage: res.schoolTeamCoverage || payload.schoolTeamCoverage || null,
          source: 'backend',
          loadError: null,
        })
      })
      .catch((err) => {
        /* ⚠️ A FAILED SCOPE READ IS NOT AN EMPTY SCOPE, AND THIS IS WHERE THE
         * TWO USED TO BECOME INDISTINGUISHABLE. Falling back to
         * `{ role: null, book: null }` silently is what let a 401 and a
         * genuinely-empty object paint the same confident 0. The error is kept
         * so the count cards can say "could not load" instead of "none". */
        if (!cancelled) {
          setGrant({
            role: null, book: null, bookMeta: null, permissions: null, source: 'session',
            loadError: String(err?.message || err) || 'the scope could not be read',
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [isAuthed, user])

  /* -- The caller's own grants, read from the grant store itself ---------
   *
   * GET /api/permissions/me is where `cc_rep_assign` and `cc_lane_delegate`
   * actually live. /api/access/me carries role and book; it is not a grant
   * store, so reading Manage Assignments off it alone meant a real grant was
   * invisible to the client. A failure here leaves both grants unresolved,
   * which draws read-only, never a live-looking control the server refuses. */
  const [myGrants, setMyGrants] = useState(null)
  const [assignmentPermissionState, setAssignmentPermissionState] = useState('loading')
  useEffect(() => {
    if (!isAuthed) { setMyGrants(null); setAssignmentPermissionState('unknown'); return }
    let cancelled = false
    setMyGrants(null)
    setAssignmentPermissionState('loading')
    api.permissionsMe()
      .then((res) => { if (!cancelled) {
        setMyGrants(res || null)
        setAssignmentPermissionState(typeof res?.canManageAssignments === 'boolean' ? 'ready' : 'error')
      } })
      .catch(() => { if (!cancelled) { setMyGrants(null); setAssignmentPermissionState('error') } })
    return () => { cancelled = true }
  }, [isAuthed, user, grantTick])

  const laneDelegation = useMemo(() => resolveLaneDelegation(myGrants), [myGrants])

  const preview = previewRole()

  const ctx = useMemo(
    () =>
      makeContext({
        user,
        role: preview || grant.role,
        // Defensive: the book DTO is unspecified until the backend ships it.
        book: Array.isArray(grant.book) ? new Set(grant.book.map(String)) : null,
        actingAs,
      }),
    [user, preview, grant.role, grant.book, actingAs]
  )

  // The observer fires outside React's render, so it reads the live context
  // through a ref rather than a captured closure.
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx

  /* -- Audit every write ----------------------------------------------- */

  useEffect(() => {
    const observerGeneration = getSessionGeneration()
    const dispose = setWriteObserver((record) => {
      const generation = getSessionGeneration()
      if (observerGeneration !== generation) return
      const c = ctxRef.current
      const meta = record.meta || {}
      recordAudit({
        actor: c.user
          ? { id: c.user.id ?? null, username: c.user.username ?? null, name: c.user.name ?? null }
          : null,
        actorRole: c.role,
        actingAs: c.actingAs
          ? { id: c.actingAs.id, name: c.actingAs.name }
          : null,
        action: meta.action || `${record.method} ${record.path}`,
        entity: meta.entity || null,
        entityId: meta.entityId ?? null,
        method: record.method,
        path: record.path,
        // `before` is only ever as good as what the call site passed; `after`
        // is the write's own payload — intent, plus the server's answer.
        before: meta.before ?? null,
        after: meta.after ?? record.body ?? null,
        ok: record.ok,
        status: record.status,
        error: record.error || null,
      }, {generation}).then(() => {
        if (generation !== getSessionGeneration()) return
        setAuditState({ pending: pendingCount(), sink: sinkStatus() })
      })
    })
    return dispose
  }, [])

  /* -- Acting as -------------------------------------------------------- */

  const startActingAs = useCallback(
    async (account) => {
      const generation = getSessionGeneration()
      const target = normalizeResource(account)
      if (!target) return { ok: false, error: 'No account given.' }
      // Client-side gate — UX only; the server gate is api.actingStart below.
      if (!rawCanActAs(ctxRef.current, account)) {
        return { ok: false, error: rawDenialReason(ctxRef.current, ACTIONS.ACTING_START, account) }
      }

      let serverGranted = false
      try {
        await api.actingStart({ accountId: target.id, accountName: target.name })
        serverGranted = true
      } catch (err) {
        // 404 = the authorization route isn't deployed. We proceed so the
        // pattern is usable, but the session is flagged `serverGranted: false`
        // and the banner says so — no pretending this was authorized.
        if (err?.status !== 404) {
          return { ok: false, error: err?.message || 'Could not take control.' }
        }
      }

      if (generation !== getSessionGeneration()) return {ok: false, error: 'The signed-in session changed. No new acting-as session was retained.'}
      const next = { ...target, since: new Date().toISOString(), serverGranted }
      setActingAs(next)
      saveActing(next)
      // The control action is itself audited, not just the writes it enables.
      await recordAudit({
        actor: ctxRef.current.user,
        actorRole: ctxRef.current.role,
        actingAs: { id: target.id, name: target.name },
        action: 'acting-as.start',
        entity: 'account',
        entityId: target.id,
        before: null,
        after: { accountId: target.id, accountName: target.name, serverGranted },
        ok: true,
      }, {generation})
      if (generation !== getSessionGeneration()) return {ok: false, error: 'The session changed; audit outcome is not confirmed here.'}
      setAuditState({ pending: pendingCount(), sink: sinkStatus() })
      return { ok: true, serverGranted }
    },
    []
  )

  const stopActingAs = useCallback(async () => {
    const generation = getSessionGeneration()
    const current = ctxRef.current.actingAs
    setActingAs(null)
    saveActing(null)
    if (!current) return
    try {
      await api.actingStop({ accountId: current.id })
    } catch {
      /* 404 / offline — the client-side session is already released */
    }
    if (generation !== getSessionGeneration()) return
    await recordAudit({
      actor: ctxRef.current.user,
      actorRole: ctxRef.current.role,
      actingAs: { id: current.id, name: current.name },
      action: 'acting-as.stop',
      entity: 'account',
      entityId: current.id,
      ok: true,
    }, {generation})
    if (generation !== getSessionGeneration()) return
    setAuditState({ pending: pendingCount(), sink: sinkStatus() })
  }, [])

  // Signing out must never leave an impersonation session behind.
  useEffect(() => {
    if (!isAuthed && actingAs) {
      setActingAs(null)
      saveActing(null)
    }
  }, [isAuthed, actingAs])

  /* -- Manage Assignments ------------------------------------------------ */

  /**
   * THE ASSIGNMENT GATE. Resolved once here so every surface that offers a rep
   * picker — the row, the bulk bar, the drawer — asks the same question and
   * gets the same answer, including the reason when it is no.
   *
   * The policy matrix (exec only) is the floor; a per-user grant from the
   * backend, or an exec's toggle, is what moves it.
   */
  const manageAssignments = useMemo(
    () =>
      resolveManageAssignments({
        user: ctx.user,
        role: ctx.role,
        policyAllows: rawCan(ctx, MANAGE_ASSIGNMENTS),
        // The grant store's own boolean wins when it answered; the access
        // payload's permissions are the older path and stay as the fallback.
        authoritativeAllowed: myGrants === null ? false : myGrants.canManageAssignments,
        permissions: grant.permissions,
      }),
    // `grantTick` is the dependency that makes a flipped toggle take effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx, grant.permissions, myGrants, grantTick]
  )

  /** Grant (`true`) / revoke (`false`) / clear (`null`) for one subject key. */
  const setAssignmentGrant = useCallback((subject, on) => {
    setAssignmentOverride(subject, on)
    setGrantTick((n) => n + 1)
  }, [])

  /* -- Bound helpers ---------------------------------------------------- */

  const value = useMemo(
    () => ({
      ctx,
      role: ctx.role,
      roleMeta: ROLE_META[ctx.role],
      roleSource: preview ? 'preview' : grant.source,
      hasRealRole: ctx.role !== ROLES.UNSCOPED,
      /* ⚠️ EXPOSED SO A ZERO CAN EXPLAIN ITSELF. `bookMeta` is the backend's own
       * account of why this caller's view is what it is, and `scopeError` is the
       * difference between "none" and "we could not ask". Both are passed to
       * `emptyReason()` by any surface that renders a count — see
       * src/lib/emptyReason.js. Deliberately NOT defaulted to `{}` or `0`:
       * absent must stay absent so the UI says "not reported" rather than
       * inventing a reason. */
      bookMeta: grant.bookMeta,
      schoolTeamCoverage: grant.schoolTeamCoverage || null,
      scopeError: grant.loadError,
      book: grant.book,
      actingAs,
      isActing: !!actingAs,
      can: (action, resource) => rawCan(ctx, action, resource),
      canActAs: (account) => rawCanActAs(ctx, account),
      why: (action, resource) => rawDenialReason(ctx, action, resource),
      visibleNav: (nav) => rawVisibleNav(ctx, nav),
      startActingAs,
      stopActingAs,
      audit: auditState,
      // "Manage Assignments" — `{ allowed, source, persisted, why }`.
      manageAssignments,
      canManageAssignments: manageAssignments.allowed,
      assignmentPermissionState,
      refreshPermissions,
      setAssignmentGrant,
      // "Cheer Lane Delegation" — `{ allowed, granted, lane }`. Drawing only;
      // the assignment lock on the backend re-decides every move.
      laneDelegation,
      userSubject,
      roleSubject,
      ACTIONS,
      ACTION_META,
    }),
    [
      ctx, preview, grant.source, grant.bookMeta, grant.schoolTeamCoverage, grant.loadError, grant.book,
      actingAs, startActingAs, stopActingAs, auditState, assignmentPermissionState,
      manageAssignments, setAssignmentGrant, laneDelegation, refreshPermissions,
    ]
  )

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
}

/** The access context or null, for components that may render outside the
 *  provider (harnesses). Never throws. */
export function useAccessOptional() {
  return useContext(AccessContext)
}

export function useAccess() {
  const ctx = useContext(AccessContext)
  if (!ctx) throw new Error('useAccess must be used within <AccessProvider>')
  return ctx
}

export { ACTIONS, ROLES, ROLE_META }
